import React, { useMemo } from 'react'
import clsx from 'clsx'
import * as PropTypes from 'prop-types'
import { flexRender } from '@tanstack/react-table'
import Typography from '../Typography'
import { Container, Grid, Icon, Spinner } from '../index'
import './list.scss'
import styles from './list.module.scss'

const List = ({
  loading = false,
  table,
  config: {
    className = '',
    skin = 'default',
    showHeader = true,
    showFooter = false,
    RenderSubComponent = () => null,
  },
  EmptyComponent = null,
  Loader = () => <Spinner width={20} />,
}) => {
  const isCompactSkin = useMemo(() => skin === 'compact', [skin])
  return (
    <>
      <table className={clsx(styles[skin], className)}>
        {showHeader && (
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <React.Fragment key={'headerGroup-' + headerGroup.id}>
                <tr>
                  {headerGroup.headers.map((header, i) => (
                    <th
                      key={'header-th-' + i}
                      colSpan={header.colSpan}
                      role={'columnheader'}
                      style={{
                        width:
                          header.column.getSize() !== 0
                            ? header.column.getSize()
                            : undefined,
                      }}
                      className={header.column.columnDef.className}
                    >
                      {header.isPlaceholder ? null : (
                        <Typography
                          component={
                            <Grid gap={8} wrap={'nowrap'} flexAlign={'center'}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                              {header.column.getCanSort() ? (
                                <Container
                                  className={styles['sorting-icon']}
                                  onClick={header.column.getToggleSortingHandler()}
                                  display={'flex'}
                                  flexDirection={'column'}
                                  flexJustify={'center'}
                                >
                                  {header.column.getIsSorted() === 'asc' ? (
                                    <Icon name={'arrow-linedown'} width={20} />
                                  ) : header.column.getIsSorted() === 'desc' ? (
                                    <Icon name={'arrow-lineup'} width={20} />
                                  ) : (
                                    <Icon name={'sort-all'} width={20} />
                                  )}
                                </Container>
                              ) : undefined}
                            </Grid>
                          }
                          variant={'Body-20'}
                        />
                      )}
                    </th>
                  ))}
                </tr>
                {!isCompactSkin && (
                  <tr role={'spacer'} key={'spacer-tr-' - headerGroup.id}>
                    <th
                      key={'spacer-th-' - headerGroup.id}
                      colSpan={headerGroup.headers.length}
                    />
                  </tr>
                )}
              </React.Fragment>
            ))}
          </thead>
        )}
        <tbody>
          {!loading && table.getRowCount() === 0 && EmptyComponent && (
            <tr>
              <td colSpan={table.getVisibleFlatColumns().length}>
                <EmptyComponent />
              </td>
            </tr>
          )}

          {table.getRowModel().rows.map((row, i) => (
            <React.Fragment key={'tbody-row-fragment-' + i}>
              {!isCompactSkin && i !== 0 && (
                <tr role={'spacer'} key={'tbody-row-spacer' - i}>
                  <td
                    colSpan={row.getVisibleCells().length}
                    key={'spacer-cell-' + i}
                  />
                </tr>
              )}
              <tr
                key={'tbody-row-' + row.id}
                role={'row'}
                className={row.getIsExpanded() ? 'row-expanded' : ''}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={'tbody-row-' + row.id + '-cell-' + cell.id}
                    role={'cell'}
                    style={{
                      width:
                        cell.column.getSize() !== 0
                          ? cell.column.getSize()
                          : undefined,
                    }}
                    className={cell.column.columnDef.className}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {row.getIsExpanded() && (
                <RenderSubComponent
                  key={'tbody-row-' + row.id + '-subrow-' + row.id}
                  row={row}
                  table={table}
                  keyRowPrefix={'tbody-row-' + row.id + '-subrow'}
                />
              )}
            </React.Fragment>
          ))}
        </tbody>
        {showFooter && (
          <tfoot>
            {table.getFooterGroups().map((footerGroup, i) => (
              <tr role={'row'} key={'footer-' + i}>
                {footerGroup.headers.map((header, j) => (
                  <td
                    key={'footer-td-' + j}
                    colSpan={header.colSpan}
                    role={'columnheader'}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
      {loading && table.getRowCount() === 0 && Loader && <Loader />}
    </>
  )
}

List.propTypes = {
  table: PropTypes.object,
  config: PropTypes.object,
  EmptyComponent: PropTypes.elementType,
  Loader: PropTypes.elementType,
  loading: PropTypes.bool,
}

export default List
